import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { AppContext } from "../contexts/AppContext";
import useRestricted from "../hooks/useRestricted";
import ImageColumGrid from "../components/common/ImageColumGrid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Snackbar from "@mui/material/Snackbar";
import { cancelSubscriptionUrl } from "../services/OrdersService";

const SubscriptionManagement: React.FC = observer(() => {
    useRestricted();

    const intl = useIntl();
    const navigate = useNavigate();
    const { userService, ordersService } = React.useContext(AppContext);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [canceling, setCanceling] = useState(false);
    const [cancelSuccess, setCancelSuccess] = useState(false);
    const [cancelError, setCancelError] = useState(false);

    const subscriptionType = ordersService.subscriptionType;
    const nextPaymentDate = userService.user?.proExpDate || null;

    useEffect(() => {
        const loadSubscriptionData = async () => {
            if (!userService.user?.id || !userService.user?.email) {
                return;
            }

            try {
                setError(null);
                await ordersService.loadSubscription(
                    userService.user.id,
                    userService.user.email,
                    userService.user.proExpDate
                );
            } catch (error) {
                console.error("Error loading subscription data:", error);
                setError("error.loadingSubscription");
            } finally {
                setLoading(false);
            }
        };

        loadSubscriptionData();
    }, [userService.user?.id, userService.user?.email, ordersService]);

    const handleBack = () => {
        navigate("/account");
    };

    const handleOpenCancelDialog = () => {
        setCancelDialogOpen(true);
    };

    const handleCloseCancelDialog = () => {
        setCancelDialogOpen(false);
    };

    const handleConfirmCancel = async () => {
        setCanceling(true);
        setCancelDialogOpen(false);

        try {
            const response = await fetch(cancelSubscriptionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: userService.user?.email,
                    plan_id: subscriptionType === "monthly" ? 2412 : subscriptionType === "annual" ? 2367 : null,
                }),
            });

            if (response.ok) {
                setCancelSuccess(true);
            } else {
                setCancelError(true);
            }
        } catch (error) {
            console.error("Error canceling subscription:", error);
            setCancelError(true);
        } finally {
            setCanceling(false);
        }
    };

    const handleChangeSubscription = () => {
        const subject = encodeURIComponent("Change Subscription Request");
        const body = encodeURIComponent(
            `Hi Time on Trails Support,\n\n` +
            `I would like to change my subscription.\n\n` +
            `User: ${userService.user?.displayName || userService.user?.email}\n` +
            `Email: ${userService.user?.email}\n` +
            `Current Subscription: ${subscriptionType === "monthly" ? "Monthly" : "Annual"}\n\n` +
            `Please assist me with changing my subscription.\n\n` +
            `Thank you!`
        );
        window.location.href = `mailto:support@timeontrails.com?subject=${subject}&body=${body}`;
    };

    if (loading) {
        return (
            <ImageColumGrid image={`${process.env.PUBLIC_URL}/images/auth-background.jpg`}>
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
                    <CircularProgress />
                </Box>
            </ImageColumGrid>
        );
    }

    return (
        <ImageColumGrid
            image={`${process.env.PUBLIC_URL}/images/auth-background${userService.user?.preferedSport === "bike" ? "-bike" : ""}.jpg`}
        >
            <Box
                sx={{
                    my: 8,
                    mx: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Button
                    startIcon={<ArrowBackIcon />}
                    sx={{ alignSelf: "flex-start", mb: 2 }}
                    onClick={handleBack}
                    color="inherit"
                >
                    {intl.formatMessage({ id: 'action.back' })}
                </Button>

                <Typography component="h1" variant="h5" gutterBottom>
                    {intl.formatMessage({ id: "subscription.title" })}
                </Typography>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mt: 2, width: '100%', maxWidth: 500 }}
                    >
                        {intl.formatMessage({ id: error })}
                    </Alert>
                )}

                {cancelSuccess && (
                    <Alert severity="success" sx={{ mt: 2, width: '100%', maxWidth: 500 }}>
                        {intl.formatMessage({ id: "subscription.cancelSuccess" })}
                    </Alert>
                )}

                {!!subscriptionType && <Box mt={4} width="100%">
                    <Box>
                        {subscriptionType === "lifetime" && (
                            <Box textAlign="center" py={3}>
                                <Typography variant="h6" color="primary" gutterBottom>
                                    {intl.formatMessage({ id: "subscription.lifetimeTitle" })}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                                    {intl.formatMessage({ id: "subscription.lifetimeMessage" })}
                                </Typography>
                            </Box>
                        )}

                        {subscriptionType === "monthly" && (
                            <>
                                <Typography variant="h6" gutterBottom>
                                    {intl.formatMessage({ id: "subscription.monthlyTitle" })}
                                </Typography>
                                {nextPaymentDate && (
                                    <Box mb={3}>
                                        <Typography variant="body2" color="text.secondary">
                                            {intl.formatMessage({ id: "subscription.nextPayment" })}
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {intl.formatDate(nextPaymentDate, { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </Typography>
                                    </Box>
                                )}
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleChangeSubscription}
                                        fullWidth
                                        disabled={canceling}
                                    >
                                        {intl.formatMessage({ id: "subscription.changeSubscription" })}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleOpenCancelDialog}
                                        fullWidth
                                        disabled={canceling}
                                    >
                                        {canceling ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            intl.formatMessage({ id: "subscription.cancelSubscription" })
                                        )}
                                    </Button>
                                </Box>
                            </>
                        )}

                        {subscriptionType === "annual" && (
                            <>
                                <Typography variant="h6" gutterBottom>
                                    {intl.formatMessage({ id: "subscription.annualTitle" })}
                                </Typography>
                                {nextPaymentDate && (
                                    <Box mb={3}>
                                        <Typography variant="body2" color="text.secondary">
                                            {intl.formatMessage({ id: "subscription.nextPayment" })}
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {intl.formatDate(nextPaymentDate, { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </Typography>
                                    </Box>
                                )}
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleChangeSubscription}
                                        fullWidth
                                        disabled={canceling}
                                    >
                                        {intl.formatMessage({ id: "subscription.changeSubscription" })}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleOpenCancelDialog}
                                        fullWidth
                                        disabled={canceling}
                                    >
                                        {canceling ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            intl.formatMessage({ id: "subscription.cancelSubscription" })
                                        )}
                                    </Button>
                                </Box>
                            </>
                        )}

                        {subscriptionType === "special" && (
                            <Box textAlign="center" py={3}>
                                <Typography variant="h6" color="primary" gutterBottom>
                                    {intl.formatMessage({ id: "subscription.specialTitle" })}
                                </Typography>
                                {nextPaymentDate && (
                                    <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                                        {intl.formatMessage(
                                            { id: "subscription.specialMessage" },
                                            { date: intl.formatDate(nextPaymentDate, { year: 'numeric', month: 'long', day: 'numeric' }) }
                                        )}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
                }
            </Box>

            {/* Cancel Confirmation Dialog */}
            <Dialog
                open={cancelDialogOpen}
                onClose={handleCloseCancelDialog}
            >
                <DialogTitle>
                    {intl.formatMessage({ id: "subscription.cancelDialogTitle" })}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {intl.formatMessage({ id: "subscription.cancelDialogMessage" })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCancelDialog} color="primary">
                        {intl.formatMessage({ id: "nav.back" })}
                    </Button>
                    <Button onClick={handleConfirmCancel} variant="contained">
                        {intl.formatMessage({ id: "subscription.confirmCancel" })}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Error Snackbar */}
            <Snackbar
                open={cancelError}
                autoHideDuration={10000}
                onClose={() => setCancelError(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setCancelError(false)} sx={{ width: '100%' }}>
                    {intl.formatMessage({ id: "subscription.cancelError" })}
                </Alert>
            </Snackbar>
        </ImageColumGrid>
    );
});

export default SubscriptionManagement;